import React from 'react';

export type IconAppearance = 'regular' | 'filled' | 'color';

export type SizeMap = Readonly<Record<number, readonly string[]>>;

/** цветной вариант хранится готовой разметкой: у него градиенты, а не один контур */
export type MarkupMap = Readonly<Record<number, string>>;

export type GlyphSet = Readonly<{regular: SizeMap; filled?: SizeMap; color?: MarkupMap}>;

type ExtractSize<K> = K extends `${infer N extends number}` ? N : K extends number ? K : never;
type SizeOf<G extends GlyphSet> = ExtractSize<keyof G['regular']>;
type AppearanceOf<G extends GlyphSet> = Extract<keyof G, IconAppearance>;

export type IconProps<G extends GlyphSet> = Omit<React.SVGProps<SVGSVGElement>, 'children'> & {
	appearance?: AppearanceOf<G>;
	/** размер в пикселях; он же задаёт width и height */
	size?: SizeOf<G> | `${SizeOf<G>}`;
};

const PREFERRED_SIZE = 20;

function nearest(available: number[], requested: number): number {
	let best = available[0];
	for (const size of available) {
		const diff = Math.abs(size - requested);
		const bestDiff = Math.abs(best - requested);
		if (diff < bestDiff || (diff === bestDiff && size > best)) best = size;
	}
	return best;
}

/**
 * Идентификаторы градиентов в файлах Fluent одинаковы у всех копий иконки, а в документе они должны
 * быть уникальны, иначе вторая копия ссылается на градиент первой и пропадает при её удалении.
 */
function withUniqueIds(markup: string, unique: string): string {
	const ids = new Set<string>();
	for (const match of markup.matchAll(/id="([^"]+)"/g)) ids.add(match[1]);
	let result = markup;
	for (const id of ids) {
		result = result.split(`id="${id}"`).join(`id="${id}_${unique}"`).split(`url(#${id})`).join(`url(#${id}_${unique})`);
	}
	return result;
}

const icons = new WeakSet<object>();

/** элемент — иконка из набора, то есть умеет appearance */
export const isIconElement = (node: React.ReactNode): node is React.ReactElement<IconProps<GlyphSet>> =>
	React.isValidElement(node) && icons.has(node.type as object);

export function createIcon<G extends GlyphSet>(displayName: string, glyphs: G) {
	const Icon: React.FC<IconProps<G>> = ({appearance, size, ...props}) => {
		const unique = React.useId().replace(/[^a-zA-Z0-9]/g, '');
		const colored = appearance === 'color' ? (glyphs as GlyphSet).color : undefined;
		const outlined = appearance === 'filled' ? (glyphs as GlyphSet).filled : undefined;
		const set: SizeMap = outlined || glyphs.regular;
		const available = Object.keys(colored || set).map(Number);
		const requested = Number(size) || nearest(available, PREFERRED_SIZE);
		const drawn = nearest(available, requested);

		if (colored) {
			return (
				<svg
					aria-hidden="true"
					focusable="false"
					width={requested}
					height={requested}
					viewBox={`0 0 ${drawn} ${drawn}`}
					fill="none"
					{...props}
					dangerouslySetInnerHTML={{__html: withUniqueIds(colored[drawn], unique)}}
				/>
			);
		}

		return (
			<svg
				aria-hidden="true"
				focusable="false"
				width={requested}
				height={requested}
				viewBox={`0 0 ${drawn} ${drawn}`}
				fill="currentColor"
				{...props}
			>
				{set[drawn].map((d, index) => (
					<path key={index} d={d} />
				))}
			</svg>
		);
	};
	Icon.displayName = displayName;
	icons.add(Icon);
	return Icon;
}
